/* eslint-disable import/max-dependencies */
import type { FC, ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import checkbox from "../../styles/checkbox";
import { TextFieldForm } from "../TextFieldForm/TextFieldForm";
import { onKeyWrapper } from "../../utils";

const StyledCheckbox = withStyles(checkbox)(Checkbox);
export type Props = {
  value: any;
  onChange: (arg0: any) => any;
  name: string;
  label?: string;
  classes?: any;
  onClose?: () => any;
  formControlSx?: any;
  placeholder?: string;
  options: string[];
  setOptions: (arg0: string[]) => any;
  ListWrapper?: FC<any>;
};

const DefaultListWrapper: any = ({
  children,
  sx,
  className,
}: {
  children: ReactNode;
  sx: any;
  className: any;
}): any => (
  <Box sx={sx} className={className}>
    {children}
  </Box>
);

const TextFieldMultiselectBody: FC<Props> = ({
  value,
  onChange,
  name,
  label,
  classes = {},
  onClose = (): any => null,
  formControlSx = {},
  placeholder,
  ListWrapper = DefaultListWrapper,
  options = [],
  setOptions,
}) => {
  const [anchorEl, setAnchorEl]: any = useState<null | HTMLElement>(null);
  const ref: any = useRef();

  function onDelete(item: string) {
    onChange(value.filter((i: string): boolean => i !== item));
  }

  function onAddNew(item: string) {
    if (!value.includes(item)) {
      onChange([...value, item]);
    }

    if (!options.includes(item)) {
      setOptions([...options, item]);
    }
  }

  const [inputValue, setInputValue]: any = useState<string>("");

  const onValueChange: (arg0: string) => any = (v: string): any =>
    setInputValue(v);

  const handleToggle: (arg0: string) => any = (item: string): any => {
    if (value.includes(item)) {
      onDelete(item);
    }
    else {
      onAddNew(item);
    }
  };

  const onEnter: any = onKeyWrapper("Enter")(() => {
    const trimmedValue: string = inputValue.trim();

    if (trimmedValue) {
      onAddNew(trimmedValue);
      setInputValue("");
    }
  });
  useEffect(() => {
    ref.current && setAnchorEl(ref.current.children[1]);
  }, [ref]);
  const allOptions: string[] = Array.from(new Set([...options, ...value]));
  const hasOptions: boolean = allOptions.length > 0;
  return (
    <ClickAwayListener onClickAway={onClose}>
      <div ref={ref}>
        <Box
          display="flex"
          justifyContent="space-between"
          className={classes.header}
          pb={1.5}
        >
          <Box>
            <Typography variant="h5">
              {label}
            </Typography>
          </Box>
          <Box>
            <Button
              data-testid="clear-all"
              variant="gradientText"
              onClick={() => onChange([])}
            >
              Clear all
            </Button>
          </Box>
        </Box>
        <TextFieldForm
          autoFocus
          sx={{
            zIndex: "modal",
          }}
          formControlSx={formControlSx}
          name={name}
          label=""
          isAuthenticated
          className={classes.inputField}
          placeholder={placeholder}
          value={inputValue}
          onChange={onValueChange}
          onKeyPress={onEnter}
          inputProps={{
            maxLength: 256,
          }}
        />
        <ListWrapper
          className={classes.popper}
          open={Boolean(anchorEl)}
          sx={{
            zIndex: "modal",
            width: anchorEl && anchorEl.clientWidth,
          }}
          anchorEl={anchorEl}
          placement="bottom-start"
        >
          {hasOptions && (
            <List
              sx={{
                width: "100%",
                maxHeight: "40vh",
                overflowY: "auto",
              }}
            >
              {allOptions.map((item: string) => (
                <MenuListItem
                  key={item}
                  onChange={handleToggle}
                  item={item}
                  checked={value.includes(item)}
                />
              ))}
            </List>
          )}
        </ListWrapper>
      </div>
    </ClickAwayListener>
  );
};

function MenuListItem({
  item,
  checked,
  onChange,
}: {
  item: string;
  checked: boolean;
  onChange: (arg0: string) => any;
}) {
  return (
    <ListItem disablePadding key={item}>
      <ListItemButton
        sx={{
          padding: "6px",
        }}
        onClick={(): any => onChange(item)}
        dense
      >
        <StyledCheckbox
          sx={{
            padding: 0,
            marginRight: "8px",
          }}
          size="small"
          checked={checked}
          tabIndex={-1}
          disableRipple
          inputProps={{
            "aria-labelledby": item,
          }}
        />
        <ListItemText
          sx={{
            mt: 0,
            mb: 0,
          }}
          id={item}
          primary={item}
          primaryTypographyProps={{
            style: {
              fontWeight: 400,
              fontSize: "1rem",
              lineHeight: 1.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

export default TextFieldMultiselectBody;
